import { Injectable, NgZone } from '@angular/core';
import { Subject } from 'rxjs';
import { IOrderModel } from 'src/app/appModels/IOrderModel';
import { LogService } from 'src/app/appServices/log.service';
import { ToasterService } from 'src/app/appServices/toaster.service';
import { TranslationService } from 'src/app/appServices/translation.service';
import { StorageService } from 'src/app/appServices/storage.service';
import { StorageKeys } from 'src/app/appServices/storage-keys';
import { DateTimeUtility } from 'src/app/appUtilities/dateTime.utility';

declare var epson: any;
declare global {
  interface Window {
    AndroidPrinter?: any;
    onPrintResult?: (status: string) => void;
  }
}

@Injectable({ providedIn: 'root' })
export class PrintEpsonService {
  private ePosDev: any;
  private printer: any = null;
  private cachedBasicInfo: any = null; // Cache basic info for synchronous access
  private cachedStore: any = null; // Cache store for synchronous access

  connected = false;
  connecting = false;
  printerlogs = new Subject<string>();

  constructor(
    public dateTimeUtility: DateTimeUtility,
    private toaster: ToasterService,
    private translationService: TranslationService,
    private logService: LogService,
    private ngZone: NgZone,
    private storageService: StorageService
  ) {
    this.resetPrinterState();
    this.loadBasicInfo();
    this.loadStore();
  }

  /**
   * Load and cache basic info for synchronous access
   */
  private async loadBasicInfo(): Promise<void> {
    try {
      this.cachedBasicInfo =
        (await this.storageService.getItem<any>(StorageKeys.BASIC_INFO)) || {};
    } catch (error) {
      console.error('Failed to load basic info:', error);
      this.cachedBasicInfo = {};
    }
  }

  /**
   * Load and cache store for synchronous access
   */
  private async loadStore(): Promise<void> {
    try {
      this.cachedStore =
        (await this.storageService.getItem<any>(StorageKeys.STORE)) || null;
    } catch (error) {
      console.error('Failed to load store:', error);
      this.cachedStore = null;
    }
  }

  /**
   * Refresh cached basic info (call when basic info changes)
   */
  async refreshBasicInfo(): Promise<void> {
    await this.loadBasicInfo();
  }

  /**
   * Refresh cached store (call when store changes)
   */
  async refreshStore(): Promise<void> {
    await this.loadStore();
  }

  resetPrinterState() {
    if (this.printer) {
      this.ePosDev.deleteDevice(this.printer, () => {});
      this.printer = null;
    }
    this.ePosDev = new epson.ePOSDevice();
    this.connected = false;
    this.connecting = false;
  }

  private log(msg: string) {
    console.log('PrintLogs:', msg);
    this.printerlogs.next(msg);
  }

  connectToPrinter(
    conntectinType: string,
    ip: string,
    brand: string,
    port = '80',
    callback?: (status: string) => void
  ) {
    this.connecting = true;
    this.log(this.translationService.translate('CONNECTING_TO_PRINTER'));
    if (window.AndroidPrinter) {
      try {
        this.androidPrint(conntectinType, ip, brand);
        window.onPrintResult = (message: string) => {
          this.log(
            this.translationService.translate('ANDROID_PRINT_RESULT') +
              ': ' +
              message
          );
          const isSuccess = !message.startsWith('Error');
          this.ngZone.run(() => {
            this.connecting = false;
            this.connected = isSuccess;
            if (!isSuccess) {
              const payload = {
                message,
                device: brand,
                ip,
                conntectinType,
              };
              this.saveErrorLogs(payload);
            }
            callback?.(isSuccess ? 'connected' : 'failed');
          });
        };
      } catch (error) {
        this.ngZone.run(() => {
          this.log(
            this.translationService.translate(
              'ANDROID_PRINTER_CONNECTION_FAILED'
            ) +
              ': ' +
              error
          );
          const payload = {
            error,
            device: brand,
            ip,
            conntectinType,
          };
          this.saveErrorLogs(payload);
          this.connecting = false;
          this.connected = false;
          callback?.('failed');
        });
      }
    } else {
      try {
        this.ePosDev.connect(ip, port, (result: string) =>
          this.callbackConnect(result, callback)
        );
      } catch (error) {
        this.log(
          this.translationService.translate('WEB_PRINTER_CONNECTION_FAILED') +
            ': ' +
            error
        );
        this.connecting = false;
        this.connected = false;
        const payload = {
          error,
          device: brand,
          ip,
          conntectinType,
        };
        this.saveErrorLogs(payload);
        callback?.('failed');
      }
    }
  }

  private callbackConnect(result: string, callback?: (status: string) => void) {
    if (result === 'OK' || result === 'SSL_CONNECT_OK') {
      this.log(this.translationService.translate('CONNECTED_TO_EPOS_DEVICE'));
      this.ePosDev.createDevice(
        'local_printer',
        this.ePosDev.DEVICE_TYPE_PRINTER,
        { crypto: false, buffer: false },
        (device: any, code: string) =>
          this.callbackCreateDevice(device, code, callback)
      );
    } else {
      this.connecting = false;
      this.log(
        this.translationService.translate('CONNECTION_FAILED') + ` [${result}]`
      );
      const payload = {
        result,
        device: 'EPSON',
        printerType: this.ePosDev.DEVICE_TYPE_PRINTER,
      };
      this.saveErrorLogs(payload);
      callback?.('error');
    }
  }

  private callbackCreateDevice(
    device: any,
    code: string,
    callback?: (status: string) => void
  ) {
    if (code === 'OK') {
      this.printer = device;
      this.printer.timeout = 60000;
      this.printer.onreceive = (res: any) => {
        this.log(
          `${this.translationService.translate('PRINT')} ${
            res.success
              ? this.translationService.translate('SUCCESS')
              : this.translationService.translate('FAILURE')
          }${
            res.code
              ? ' ' +
                this.translationService.translate('CODE') +
                ': ' +
                res.code
              : ''
          }`
        );
        if (!res.success) {
          const payload = {
            res,
            device,
            code,
          };
          this.saveErrorLogs(payload);
          this.toaster.errorToastWithClose(
            this.translationService.translate('PRINT_FAILURE')
          );
        }
      };
      this.connected = true;
      this.connecting = false;
      this.log(this.translationService.translate('PRINTER_READY'));
      callback?.('connected');
    } else {
      this.connecting = false;
      const payload = {
        device,
        code,
      };
      this.saveErrorLogs(payload);
      this.log(
        this.translationService.translate('ERROR_CREATING_DEVICE') + ': ' + code
      );
      callback?.('error');
    }
  }

  androidPrint(
    conntectionType: string,
    printerIp: string,
    brand: string,
    order?: IOrderModel
  ) {
    if (window.AndroidPrinter) {
      order = order || this.dummyOrder;
      const orderText = this.convertOrderToPrintTextForAndroid(order);
      window.AndroidPrinter.printData(
        orderText,
        brand,
        printerIp,
        conntectionType
      );
    }
  }

  print(order?: IOrderModel) {
    if (!this.connected) {
      const payload = {
        message: 'Printer is not configured from settings',
      };
      this.saveErrorLogs(payload);
      this.toaster.warningToastWithClose(
        this.translationService.translate(
          'PLEASE_CONNECT_PRINTER_FROM_SETTINGS'
        )
      );
      return;
    }
    this.printer.addLayout(this.printer.LAYOUT_RECEIPT, 800, 0, 0, 0, 35, 0);
    this.convertOrderToPrintData(order);
    this.printer.addCut(this.ePosDev.CUT_FEED);
    this.printer.send();
  }

  private convertOrderToPrintData(order: any) {
    order = order || this.dummyOrder;
    const basicInfo = this.cachedBasicInfo || {};
    const currency = basicInfo.currencyCode || '';

    // Get translated restaurant name
    const restaurantName = this.getTranslatedText(
      basicInfo.restaurantName || 'RESTAURANT'
    );
    // Get store address from order, cached store, or basicInfo
    const storeAddress =
      order.storeAddress ||
      this.cachedStore?.address ||
      basicInfo?.address ||
      '';
    const vatNumber = `${order.vatNumber || ''}`.trim();

    this.printer.addText('\n');
    this.printer.addTextAlign(this.printer.ALIGN_CENTER);
    this.printer.addTextSmooth(true);
    this.printer.addTextDouble(true, true);
    this.printer.addText(restaurantName + '\n');
    this.printer.addTextDouble(false, false);

    // Add restaurant address if available
    if (storeAddress) {
      this.printer.addText(storeAddress + '\n');
    }
    if (vatNumber) {
      this.printer.addText(vatNumber + '\n');
    }

    this.printer.addTextSmooth(false);
    this.printer.addText(
      '\n************************************************\n'
    );

    // Get translated order type
    const orderType = this.getTranslatedOrderType(order.orderTypeModel);
    const isToGo =
      order.orderTypeModel?.type === 'To Go' ||
      order.orderTypeModel?.orderTypeName === 'To Go';

    if (isToGo) {
      this.printer.addCommand('\x1D\x42\x01');
      this.printer.addTextDouble(false, true);
      this.printer.addText(orderType + '\n');
      this.printer.addTextDouble(false, false);
      this.printer.addCommand('\x1D\x42\x00');
    } else {
      this.printer.addTextDouble(false, true);
      this.printer.addText(orderType + '\n');
      this.printer.addTextDouble(false, false);
    }
    this.printer.addText(
      '************************************************\n\n'
    );
    this.printer.addTextAlign(this.printer.ALIGN_LEFT);

    if (order.id) {
      this.printer.addTextAlign(this.printer.ALIGN_CENTER);
      this.printer.addText(this.getTranslatedText('Order Number') + '\n');
      this.printer.addTextDouble(true, true);
      this.printer.addText(order.id + '\n');
      this.printer.addTextDouble(false, false);
      this.printer.addTextAlign(this.printer.ALIGN_LEFT);
    }

    // Order Date (translated)
    const orderDateLabel = this.getTranslatedText('Order Date');
    this.printer.addText(orderDateLabel + ':');
    this.printer.addTextPosition(300);
    this.printer.addText(
      this.dateTimeUtility.getDisplayDate(order.orderDate) + '\n'
    );

    // Table Number (if exists, translated)
    const tableNumber = order.tableModel?.tableNumber || order.tableNumber;
    if (tableNumber) {
      const tableNumberLabel = this.getTranslatedText('Table Number');
      this.printer.addText(tableNumberLabel + ':');
      this.printer.addTextPosition(300);
      this.printer.addText(tableNumber + '\n');
    }

    // Payment status (translated)
    const paymentLabel = this.getTranslatedText('Payment');
    this.printer.addText(paymentLabel + ':');
    this.printer.addTextPosition(300);
    this.printer.addTextSmooth(true);
    const paymentStatus = order.paid
      ? this.getTranslatedText('Paid')
      : this.getTranslatedText('Un-Paid');
    this.printer.addText(paymentStatus + '\n');
    this.printer.addTextSmooth(false);
    // Checkout form fields (translated field names)
    order.orderCheckoutDataList?.forEach((f: any) => {
      const fieldName = this.getTranslatedText(f.fieldName);
      this.printer.addText(fieldName + ':');
      this.printer.addTextPosition(300);
      this.printer.addText(f.fieldValue + '\n');
    });
    this.printer.addText('\n');
    this.printer.addTextAlign(this.printer.ALIGN_CENTER);
    this.printer.addText(
      '************************************************\n\n'
    );
    // Order items with translations
    order.orderDetailsModelList?.forEach((item: any) => {
      const productName = this.getTranslatedProductName(item);
      const fullProductText = item.quantity + ' × ' + productName;
      const wrappedLines = this.wrapText(fullProductText, 30);
      this.printer.addTextSmooth(true);
      this.printer.addTextAlign(this.printer.ALIGN_LEFT);
      wrappedLines.forEach((line: string, idx: number) => {
        this.printer.addText(line);
        if (idx === 0) {
          this.printer.addTextPosition(450);
          this.printer.addText(currency + item.basePrice.toFixed(2) + '\n');
        } else {
          this.printer.addText('\n');
        }
      });

      // Variants (translated)
      item.orderDetailsVariantModels?.forEach((v: any) => {
        this.printer.addTextSmooth(false);
        this.printer.addTextAlign(this.printer.ALIGN_LEFT);
        const variantName = this.getTranslatedVariantName(v);
        this.printer.addText('-' + variantName);
        this.printer.addTextPosition(450);
        this.printer.addText(
          v.variantPrice > 0
            ? '+' + currency + v.variantPrice.toFixed(2) + '\n'
            : '\n'
        );
      });

      // Addons (translated)
      item.orderDetailsAddOnModelList?.forEach((a: any) => {
        this.printer.addTextSmooth(false);
        this.printer.addTextAlign(this.printer.ALIGN_LEFT);
        const addonName = this.getTranslatedAddonName(a);
        this.printer.addText('-' + addonName);
        this.printer.addTextPosition(450);
        this.printer.addText(
          a.addonPrice > 0
            ? '+' + currency + a.addonPrice.toFixed(2) + '\n'
            : '\n'
        );
      });
      if (item.productMessage)
        this.printer.addText("'" + item.productMessage + "'\n");
      this.printer.addText('\n');
    });
    this.printer.addTextSmooth(false);
    this.printer.addText('\n');
    this.printer.addTextAlign(this.printer.ALIGN_CENTER);
    this.printer.addText('************************************************\n');
    this.printer.addTextAlign(this.printer.ALIGN_LEFT);

    // Totals section (translated)
    if (order.tips) {
      const tipLabel = this.getTranslatedText('Tip');
      this.printer.addText(tipLabel + ':');
      this.printer.addTextPosition(450);
      this.printer.addText(currency + order.tips + '\n');
    }

    if (order.tax && order.tax > 0) {
      const vatLabel = this.getTranslatedText('VAT');
      if (this.isTaxIncluded(order)) {
        const vatIncludedAmount = this.calculateTaxIncludedAmount(
          order.totalAmount,
          order.tax
        );
        this.printer.addText(`${vatLabel} ${order.tax}%:`);
        this.printer.addTextPosition(450);
        this.printer.addText(currency + vatIncludedAmount.toFixed(2) + '\n');
      } else {
        const taxAmount = this.getTaxAmount(order);
        this.printer.addText(`${vatLabel} ${order.tax}%:`);
        this.printer.addTextPosition(450);
        this.printer.addText(currency + taxAmount.toFixed(2) + '\n');
      }
    }

    const totalLabel = this.getTranslatedText('Total');
    this.printer.addText(totalLabel);
    this.printer.addTextPosition(450);
    this.printer.addText(currency + order.totalAmount.toFixed(2) + '\n');
    this.printer.addTextSmooth(false);
    this.printer.addTextAlign(this.printer.ALIGN_CENTER);
    this.printer.addText('\n');

    // Cash payment details (translated, only if cash payment and change > 0)
    if (order.paymentType === 'cash' && order?.change > 0) {
      this.printer.addTextAlign(this.printer.ALIGN_LEFT);
      const paidAmountLabel = this.getTranslatedText('Paid Amount');
      this.printer.addText(paidAmountLabel);
      this.printer.addTextPosition(450);
      this.printer.addText(
        currency +
          this.calculateCashAmountPaid(order?.change, order.totalAmount) +
          '\n'
      );
      const changeLabel = this.getTranslatedText('Change');
      this.printer.addText(changeLabel);
      this.printer.addTextPosition(450);
      this.printer.addText(currency + order?.change.toFixed(2) + '\n\n');
    }
  }

  convertOrderToPrintTextForAndroid(order: any): string {
    const doubleSizeOn = '\x1D\x21\x10',
      doubleSizeOff = '\x1D\x21\x00';
    const alignCenter = '\x1B\x61\x01',
      alignLeft = '\x1B\x61\x00';
    const reverseOn = '\x1D\x42\x01',
      reverseOff = '\x1D\x42\x00';
    order = order || this.dummyOrder;
    const basicInfo = this.cachedBasicInfo || {};

    // Get translated restaurant name
    const restaurantName = this.getTranslatedText(
      basicInfo?.restaurantName || 'DUMMY RESTAURANT'
    ).toUpperCase();
    // Get store address from order, cached store, or basicInfo
    const storeAddress =
      order.storeAddress ||
      this.cachedStore?.address ||
      basicInfo?.address ||
      '';
    const vatNumber = `${order.vatNumber || ''}`.trim();
    const currency = basicInfo?.currencyCode || 'CHF';

    let receiptText =
      '\n' + alignCenter + doubleSizeOn + restaurantName + '\n' + doubleSizeOff;

    // Add restaurant address if available
    if (storeAddress) {
      receiptText += alignCenter + storeAddress + '\n';
    }
    if (vatNumber) {
      receiptText += alignCenter + vatNumber + '\n';
    }

    receiptText += alignLeft + '\n';
    receiptText += '*'.repeat(48) + '\n';

    // Get translated order type
    const orderType = this.getTranslatedOrderType(order.orderTypeModel);
    const isToGo =
      order.orderTypeModel?.type === 'To Go' ||
      order.orderTypeModel?.orderTypeName === 'To Go';

    if (isToGo) {
      receiptText +=
        alignCenter +
        reverseOn +
        doubleSizeOn +
        orderType +
        '\n' +
        doubleSizeOff +
        reverseOff;
    } else {
      receiptText +=
        alignCenter + doubleSizeOn + orderType + '\n' + doubleSizeOff;
    }

    receiptText += '*'.repeat(48) + '\n' + alignLeft + '\n';

    if (order.id) {
      receiptText +=
        alignCenter +
        this.getTranslatedText('Order Number') +
        '\n' +
        doubleSizeOn +
        order.id +
        '\n' +
        doubleSizeOff +
        alignLeft;
    }

    // Order Date (translated)
    const orderDateLabel = this.getTranslatedText('Order Date');
    receiptText +=
      `${orderDateLabel}:`.padEnd(25) +
      this.dateTimeUtility.getDisplayDate(order.orderDate) +
      '\n';

    // Table Number (if exists, translated)
    const tableNumber = order.tableModel?.tableNumber || order.tableNumber;
    if (tableNumber) {
      const tableNumberLabel = this.getTranslatedText('Table Number');
      receiptText += `${tableNumberLabel}:`.padEnd(25) + tableNumber + '\n';
    }

    // Payment status (translated)
    const paymentLabel = this.getTranslatedText('Payment');
    const paymentStatus = order.paid
      ? this.getTranslatedText('Paid')
      : this.getTranslatedText('Un-Paid');
    receiptText += `${paymentLabel}:`.padEnd(25) + paymentStatus + '\n';

    // Checkout form fields (translated)
    order.orderCheckoutDataList?.forEach((f: any) => {
      const fieldName = this.getTranslatedText(f.fieldName);
      receiptText += `${fieldName}:`.padEnd(25) + f.fieldValue + '\n';
    });
    receiptText += '\n' + '*'.repeat(48) + '\n';

    // Order items with translations
    order.orderDetailsModelList?.forEach((item: any) => {
      const productName = this.getTranslatedProductName(item);
      const nameLines = this.wrapTextForAndroid(
        `${item.quantity} × ${productName}`,
        30
      );
      nameLines.forEach((line: string, i: number) => {
        receiptText +=
          i === 0
            ? this.formatLine(line, `${currency}${item.basePrice.toFixed(2)}`)
            : line + '\n';
      });

      // Variants (translated)
      item.orderDetailsVariantModels?.forEach((v: any) => {
        const variantName = this.getTranslatedVariantName(v);
        const line = ` -${variantName}`;
        receiptText += this.formatLine(
          line,
          v.variantPrice > 0 ? `+${currency}${v.variantPrice.toFixed(2)}` : ''
        );
      });

      // Addons (translated)
      item.orderDetailsAddOnModelList?.forEach((a: any) => {
        const addonName = this.getTranslatedAddonName(a);
        const line = ` -${addonName}`;
        receiptText += this.formatLine(
          line,
          a.addonPrice > 0 ? `+${currency}${a.addonPrice.toFixed(2)}` : ''
        );
      });
      if (item.productMessage) receiptText += "'" + item.productMessage + "'\n";
      receiptText += '\n';
    });
    receiptText += '*'.repeat(48) + '\n';

    // Totals section (translated)
    if (order.tips) {
      const tipLabel = this.getTranslatedText('Tip');
      receiptText += tipLabel.padEnd(40) + `${currency}${order.tips}\n`;
    }

    if (order.tax && order.tax > 0) {
      const vatLabel = this.getTranslatedText('VAT');
      if (this.isTaxIncluded(order)) {
        const vatIncludedAmount = this.calculateTaxIncludedAmount(
          order.totalAmount,
          order.tax
        );
        receiptText +=
          `${vatLabel} ${order.tax}%`.padEnd(40) +
          `${currency}${vatIncludedAmount.toFixed(2)}\n`;
      } else {
        const taxAmount = this.getTaxAmount(order);
        receiptText +=
          `${vatLabel} ${order.tax}%`.padEnd(40) +
          `${currency}${taxAmount.toFixed(2)}\n`;
      }
    }

    const totalLabel = this.getTranslatedText('Total');
    receiptText +=
      totalLabel.padEnd(40) + `${currency}${order.totalAmount.toFixed(2)}\n`;

    // Cash payment details (translated, only if cash payment and change > 0)
    if (order.paymentType === 'cash' && order.change > 0) {
      const paidAmount = this.calculateCashAmountPaid(
        order.change,
        order.totalAmount
      );
      const paidAmountLabel = this.getTranslatedText('Paid Amount');
      receiptText +=
        paidAmountLabel.padEnd(40) + `${currency}${paidAmount.toFixed(2)}\n`;
      const changeLabel = this.getTranslatedText('Change');
      receiptText +=
        changeLabel.padEnd(40) + `${currency}${order.change.toFixed(2)}\n`;
    }
    return receiptText;
  }

  private isTaxIncluded(order: any): boolean {
    const value = order?.taxIncluded;
    if (value == null || `${value}`.trim() === '') return true;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === '1' || normalized === 'true' || normalized === 'yes';
    }
    return false;
  }

  private calculateTaxIncludedAmount(totalAmount: number, taxRate: number): number {
    const safeTotal = Number(totalAmount) || 0;
    const safeTaxRate = Number(taxRate) || 0;
    if (safeTotal <= 0 || safeTaxRate <= 0) return 0;
    return Number(((safeTotal * safeTaxRate) / (100 + safeTaxRate)).toFixed(2));
  }

  getTaxAmount(order: any) {
    const safeTaxRate = Number(order?.tax) || 0;
    if (safeTaxRate <= 0 || !Array.isArray(order?.orderDetailsModelList)) {
      return 0;
    }

    const subtotal = order.orderDetailsModelList.reduce(
      (total: number, item: any) => total + this.calculateOrderLineSubtotal(item),
      0
    );
    const tipAmount = Number(order?.tips) || 0;

    return Number((((subtotal + tipAmount) * safeTaxRate) / 100).toFixed(2));
  }

  private calculateOrderLineSubtotal(item: any): number {
    const basePrice = Number(item?.basePrice) || 0;
    const quantity = Number(item?.quantity) || 0;
    const addOnTotal = Array.isArray(item?.orderDetailsAddOnModelList)
      ? item.orderDetailsAddOnModelList.reduce((sum: number, addOn: any) => {
          const addOnPrice = Number(addOn?.addonPrice) || 0;
          const addOnQuantity = Number(addOn?.quantity) || 1;
          return sum + addOnPrice * addOnQuantity;
        }, 0)
      : 0;
    const variantTotal = Array.isArray(item?.orderDetailsVariantModels)
      ? item.orderDetailsVariantModels.reduce((sum: number, variant: any) => {
          const variantPrice = Number(variant?.variantPrice) || 0;
          const variantQuantity = Number(variant?.quantity) || 1;
          return sum + variantPrice * variantQuantity;
        }, 0)
      : 0;

    return Number((((basePrice + addOnTotal + variantTotal) * quantity) || 0).toFixed(2));
  }

  calculateCashAmountPaid(change: number, totalAmount: number) {
    return parseFloat((parseFloat(change as any) + totalAmount).toFixed(2));
  }

  disconnectFromPrinter() {
    this.ePosDev.deleteDevice(this.printer, (err: any) =>
      this.callbackDeleteDevice(err)
    );
  }

  private callbackDeleteDevice(_errorCode: any) {
    this.ePosDev.disconnect();
  }

  private formatLine(left: string, right: string, totalWidth = 48) {
    const leftWidth = totalWidth - right.length;
    return left.slice(0, leftWidth).padEnd(leftWidth) + right + '\n';
  }

  private wrapTextForAndroid(text: string, width: number) {
    const lines: string[] = [];
    while (text.length > width) {
      let wrapAt = text.lastIndexOf(' ', width);
      if (wrapAt === -1) wrapAt = width;
      lines.push(text.slice(0, wrapAt));
      text = text.slice(wrapAt).trim();
    }
    if (text) lines.push(text);
    return lines;
  }

  private wrapText(text: string, width: number) {
    const words = text.split(' ');
    const lines: string[] = [];
    let line = '';
    for (const word of words) {
      if ((line + word).length <= width) {
        line += (line ? ' ' : '') + word;
      } else {
        if (line) lines.push(line);
        line = word;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  dummyOrder = {
    id: 16142,
    orderTypeModel: { type: 'Dine In' },
    orderStatusModel: { status: 'Accepted', initialState: false },
    orderDate: 1703847054176,
    totalAmount: 28.98,
    orderDetailsModelList: [
      {
        id: 33584,
        productName: '#1: 2 Burritos + 3 Tacos + 1 Side of Rice & Beans',
        basePrice: 23.11,
        quantity: 1,
        productMessage: '',
        orderDetailsAddOnModelList: [
          {
            id: 5140,
            addonName: 'Mozzarella Jack Cheese',
            addonPrice: 0.1,
            addonId: 260,
          },
        ],
        orderDetailsVariantModels: [
          {
            id: 152930,
            childVariantName: 'Al Pastor',
            variantPrice: 0,
            defaultSelected: true,
            childVariantId: 559,
            groupId: 33651,
          },
          {
            id: 152931,
            childVariantName: 'Chicken',
            variantPrice: 0,
            defaultSelected: true,
            childVariantId: 561,
            groupId: 33651,
          },
        ],
        productId: 419,
      },
    ],
    deliveryFee: 0,
    trackOrderModels: [
      {
        id: 53271,
        orderModel: {
          id: 16142,
          orderDate: 1703847054176,
          totalAmount: 28.98,
          deliveryFee: 0,
          tax: 8.19,
          paymentType: 'cash',
          paid: true,
          tips: 0,
          discount: 0,
          storeId: 1,
          storeName: 'Restaurant_Preprod',
          storeAddress: 'CG9C3J56+FR',
          processingFee: 0,
          platformFee: 0,
          exciseTax: 0,
        },
        orderStatusModel: { id: 2, status: 'Accepted', initialState: true },
      },
    ],
    tax: 8.19,
    paymentType: 'cash',
    paid: true,
    tips: 0,
    discount: 0,
    storeId: 1,
    storeName: 'Restaurant_Preprod',
    storeAddress: 'CG9C3J56+FR',
    orderPaymentModelList: [],
    processingFee: 0,
    orderCheckoutDataList: [
      { id: 936, fieldName: 'Name', fieldValue: '4', orderId: 16142 },
    ],
    platformFee: 0,
    exciseTax: 0,
    change: 2.1,
  };

  // Helper methods for translations
  private getTranslatedText(key: string): string {
    if (!key) return '';
    const translated = this.translationService.translate(key);
    return translated !== key ? translated : key;
  }

  private getTranslatedOrderType(orderType: any): string {
    if (!orderType) return '';

    // Try orderTypeName first
    const orderTypeName = orderType.orderTypeName || orderType.type;
    if (!orderTypeName) return '';

    // Try direct translation
    const directTranslation = this.translationService.translate(orderTypeName);
    if (directTranslation !== orderTypeName) {
      return directTranslation;
    }

    // Try translateModelList if available
    if (orderType.translateModelList) {
      return this.translationService.getTranslatedValue(
        orderType,
        'Name',
        'orderTypeName'
      );
    }

    // Fallback to original
    return orderTypeName;
  }

  private getTranslatedProductName(productData: any): string {
    if (!productData?.productName) return '';

    // Try direct translation
    const directTranslation = this.translationService.translate(
      productData.productName
    );
    if (directTranslation !== productData.productName) {
      return directTranslation;
    }

    // Try translateModelList if available
    if (productData.translateModelList) {
      return this.translationService.getTranslatedValue(
        productData,
        'Name',
        'productName'
      );
    }

    return productData.productName;
  }

  private getTranslatedVariantName(variant: any): string {
    if (!variant?.childVariantName) return '';

    // Try direct translation
    const directTranslation = this.translationService.translate(
      variant.childVariantName
    );
    if (directTranslation !== variant.childVariantName) {
      return directTranslation;
    }

    // Try translateModelList if available
    if (variant.translateModelList) {
      return this.translationService.getTranslatedValue(
        variant,
        'Name',
        'childVariantName'
      );
    }

    return variant.childVariantName;
  }

  private getTranslatedAddonName(addon: any): string {
    if (!addon?.addonName) return '';

    // Try direct translation
    const directTranslation = this.translationService.translate(
      addon.addonName
    );
    if (directTranslation !== addon.addonName) {
      return directTranslation;
    }

    // Try translateModelList if available
    if (addon.translateModelList) {
      return this.translationService.getTranslatedValue(
        addon,
        'Name',
        'addonName'
      );
    }

    return addon.addonName;
  }

  /**
   * Save error logs
   * @param message
   */
  saveErrorLogs(message: any) {
    message.channel = 'Kiosk';
    const payload = {
      type: 'PRINTER',
      logs: message,
    };
    this.logService.saveLogs(payload).subscribe({
      next: (res) => {},
      error: (err) => {},
    });
  }
}
